<template>
  <div class="datascience-page">
    <!-- Header with gradient, without double navigation -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Leverage your company's data</h1>
        <p class="subtitle">Analyze data to decide your growth strategy</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Which data solutions best match your industry ?</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                Our team brings together data scientists, analysts, 
                developers, and data specialists with multi-sector experience. 
                We operate across the entire data processing cycle, 
                from analysis to visualization, including implementation 
                and deployment of dedicated tools.
              </p><br>
              <p>
                ✓ <strong><i>Predictive Analytics</i></strong> : Anticipate the future trends, optimize the decision-making processes, and forecast the customer behaviors or the market developments.
              </p><br>
              <p>
                ✓ <strong><i>Customer Segmentation and Personalization</i></strong> : Understand deeply your different customer segments to personalize your offers, enhance customer experience and customer retention strategies.
              </p><br>
              <p>
                ✓ <strong><i>Operations Optimization</i></strong> : Identify inefficiencies, reduce costs, and improve internal processes through operational data analysis and automation.
              </p>
            </div>
            <div class="youtube-container">
              <iframe
                style="width: 100%; height: 100%; border: none; border-radius: 15px;"
                src="https://www.youtube.com/embed/ZEmF4ytCdfs"
                title="Presentation of our Data Science solutions"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen>
              </iframe>
            </div>
          </div>
        </div>
      </section>

      <!-- Interactive Dashboard Illustration -->
      <section class="dashboard-illustration">
        <div class="illustration-container">
          <img src="/img/dashboard-en.svg" alt="Interactive dashboard with graphs and metrics" class="dashboard-image" />
        </div>
      </section>


      <!-- What is Data Science -->
      <section class="card what-is-card">
        <div class="card-content">
          <h2 class="section-title">What is data science ?</h2>
          <div class="text-content">
            <p>
              Data Science is the discipline that uses scientific methods, processes, 
              algorithms, and systems to extract knowledge and insights from structured 
              and unstructured data. By combining statistics, data analysis, machine 
              learning, and related techniques, Data Science enables businesses to make 
              informed decisions based on concrete data rather than intuition. In a world 
              where data is generated at an unprecedented rate, the ability to analyze it effectively 
              becomes an absolutely essential competitive advantage.
            </p>
          </div>
        </div>
      </section>

      <!-- Why choose our Data Science services -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">How can we help you valorize your data with Data Science ?</h2>
          <div class="text-content">
            <p>
              Our solutions help you improve your decision-making process through methodical analysis 
              of your data. We use technologies adapted to your specific needs to allow you 
              to effectively exploit the information at your disposal. With our approach, you will be able 
              to identify opportunities and have tangible elements to support your strategic 
              decisions.
            </p>
          </div>
        </div>
      </section>

      <!-- Advantages (Cards) -->
      <div class="advantages-grid">
        <!-- Advantage 1 -->
        <div class="advantage-card">
          <div class="advantage-number">1</div>
          <h3 class="advantage-title">Excel and VBA : The gateway to data analysis and visualization</h3>
          <p class="advantage-description">
            Excel is a powerful tool that serves as a cornerstone for data analysis across various industries. 
            Its intuitive interface and proven features make it accessible to everyone. By working with us, 
            you can harness the full potential of Excel through personalized training and support. Our expertise 
            in VBA will allow you, for example, to automate repetitive tasks and create interactive dashboards.
          </p>
        </div>

        <!-- Advantage 2 -->
        <div class="advantage-card">
          <div class="advantage-number">2</div>
          <h3 class="advantage-title">SQL and SAS : Elevate your data management and analysis to the next level</h3>
          <p class="advantage-description">
            SQL is essential for managing large volumes of data and querying relational databases. Our team 
            will work closely with you to demystify SQL and enable you to perform complex 
            queries. Coupled with SAS, you will have access to detailed analyses and business intelligence 
            tools that will transform your data into decision-making elements.
          </p>
        </div>

        <!-- Advantage 3 -->
        <div class="advantage-card">
          <div class="advantage-number">3</div>
          <h3 class="advantage-title">Python : Your premier partner in Data Science and Data Visualization</h3>
          <p class="advantage-description">
            Python has established itself as a favorite in the data science community. Our experienced team 
            will help you leverage Python to automate complex workflows, conduct advanced 
            analyses, and develop predictive models through machine learning. Together, we will elevate 
            your strategic planning and give you a definite competitive advantage.
          </p>
        </div>

        <!-- Advantage 4 -->
        <div class="advantage-card">
          <div class="advantage-number">4</div>
          <h3 class="advantage-title">Diverse industry expertise</h3>
          <p class="advantage-description">
            We have collaborated with clients in various fields (startups, e-commerce, healthcare, 
            industry, finance, insurance, etc.). We understand the challenges your company 
            faces daily and can quickly adapt to your needs. We will implement 
            custom solutions to extract, clean, and store your data, then analyze it in depth.
          </p>
        </div>

        <!-- Advantage 5 -->
        <div class="advantage-card">
          <div class="advantage-number">5</div>
          <h3 class="advantage-title">Sustainable development and environment</h3>
          <p class="advantage-description">
            We have also worked on environmental projects, including carbon assessments 
            and energy consumption. Our team can therefore help you establish your carbon footprint 
            and strengthen your CSR plan. You will thus be able to address the challenges 
            facing your company in terms of sustainable development, have 
            a positive impact and better brand image, while achieving your environmental objectives.
          </p>
        </div>

        <!-- Advantage 6 -->
        <div class="advantage-card">
          <div class="advantage-number">6</div>
          <h3 class="advantage-title">Detailed analyses and reports</h3>
          <p class="advantage-description">
            Data Science also involves creating dashboards, managing data projects, 
            strategic data consulting, data analysis (Data Mining, Data Science, Machine Learning) 
            and support for various tools and languages (Excel, SQL, Python, SAS, VBA, R). You will have access to 
            clear reports, with practical and easy-to-read visualization solutions. This way, 
            you will have directly usable decision-making elements.
          </p>
        </div>
      </div>

      <!-- Client testimonials -->
      <section class="testimonials-section">
        <h2 class="testimonials-title">What our clients say</h2>
        
        <div class="testimonials-container">
          <!-- Testimonial 1 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Marie D.</h3>
                <p class="client-company">Development Director</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Arynae's expertise in data analysis has allowed us to identify untapped 
              business opportunities and increase our ROI by 28% in six months. Their 
              methodical approach and insights have given us a significant competitive advantage.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Testimonial 2 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Pierre L.</h3>
                <p class="client-company">CEO</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>The carbon assessment conducted by Arynae has transformed our approach to sustainability. 
              Their detailed analyses allowed us to identify the main areas for improvement 
              and reduce our environmental footprint by 40% in one year.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Testimonial 3 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Sophie N.</h3>
                <p class="client-company">Logistics Director</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>The customized dashboards developed by Arynae have revolutionized our 
              decision-making. We now have real-time visibility on our 
              performance and can quickly react to market trends.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
        </div>
        

      </section>

      <!-- Call to action -->
      <div class="cta-container">
        <span class="cta-button fake-link" @click="navigateTo('e1')">Request a consultation</span>
      </div>
    </div>
  </div>
</template>

  <script>
  import { useHead } from '@vueuse/head'
  export default {
  name: 'DataSciencePage',
  setup() {
    useHead({
      title: 'Data Science services - Data analysis solutions | Arynae',
      meta: [
          { name: 'description', content: 'B2B Data Science and data analysis services. Transform your data into operational indicators for decision-making and gain a competitive advantage.' },
          { name: 'keywords', content: 'data science, data analysis, big data, business intelligence, dashboard, KPI, indicators, data visualization, python, excel, vba, sql, sas, machine learning, Arynae' },
          { name: 'robots', content: 'index, follow' },
          { name: 'author', content: 'Arynae' },
          { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
          // Essential Open Graph
          { property: 'og:type', content: 'website' },
          { property: 'og:title', content: 'Data Science services - Data analysis solutions | Arynae' },
          { property: 'og:description', content: 'B2B Data Science and data analysis services. Transform your data into operational indicators for decision-making and gain a competitive advantage.' },
          { property: 'og:url', content: 'https://arynae.com/en/datascience' },
          { property: 'og:locale', content: 'en_US' },
          { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
          { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
          // Basic Twitter Card
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: 'Data Science services - Data analysis solutions | Arynae' },
          { name: 'twitter:description', content: 'B2B Data Science and data analysis services. Transform your data into operational indicators for decision-making and gain a competitive advantage.' },
          { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
          { rel: 'canonical', href: 'https://arynae.com/en/datascience' },
          { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/datascience' },
          { rel: 'alternate', hreflang: 'en', href: 'https://arynae.com/en/datascience' }
      ],
      htmlAttrs: {
          lang: 'en'
      }
    })
      
      return {}
  },
  data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
  </script>


<style scoped>
.datascience-page {
font-family: Arial, sans-serif;
color: #333;
}

/* Header Styles */
.header {
background: linear-gradient(to right, #2563EB, #7C3AED);
padding-bottom: 6rem;
color: white;
}

.header-content {
text-align: center;
padding: 5rem 2rem 3rem; /* Augmenté le padding-top pour compenser l'absence de la navigation */
}

.main-title {
font-size: 2.75rem;
margin-bottom: 1rem;
}

.subtitle {
font-size: 1.25rem;
max-width: 800px;
margin: 0 auto;
}

/* Content Container */
.content-container {
max-width: 1200px;
margin: -3rem auto 4rem;
padding: 0 2rem;
}

/* Card Styles */
.card {
background: linear-gradient(to bottom, #ffffff, #f3f4f6);
border-radius: 0.5rem;
box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
margin-bottom: 2rem;
overflow: hidden;
}

.card-content {
padding: 2rem;
}

.section-title {
font-size: 1.75rem;
margin-bottom: 1.5rem;
color: #333;
display: block; /* Assure que le titre occupe toute la largeur */
}

.text-content {
font-size: 1rem;
line-height: 1.6;
color: #555;
}

/* Intro Card with Illustration */
.text-and-illustration {
display: flex;
flex-wrap: wrap;
gap: 2rem;
}

.text-and-illustration .text-content {
flex: 2;
min-width: 300px;
}

/* Conteneur YouTube et vidéo */
.youtube-container {
flex: 1;
min-width: 300px;
height: 250px; /* Hauteur fixe pour le conteneur */
display: flex;
justify-content: center;
align-items: center;
}

/* Advantages Grid */
.advantages-grid {
display: grid;
grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
gap: 2rem;
margin: 3rem 0;
}

.advantage-card {
background: white;
border-radius: 0.5rem;
box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
padding: 2rem;
position: relative;
}

.advantage-number {
display: flex;
justify-content: center;
align-items: center;
width: 60px;
height: 60px;
border-radius: 50%;
background: linear-gradient(to right, #2563EB, #7C3AED);
color: white;
font-size: 1.25rem;
font-weight: bold;
margin-bottom: 1rem;
}

.advantage-title {
font-size: 1.25rem;
margin-bottom: 1rem;
color: #333;
}

.advantage-description {
font-size: 0.875rem;
line-height: 1.6;
color: #555;
}

/* Testimonials Section */
/* Testimonials Section */
.testimonials-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  padding: 1rem;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  padding-bottom: 1rem; /* Espace pour la scrollbar */
}

.testimonial-card {
  flex: 0 0 calc(33.333% - 1.5rem);
  min-width: 280px;
  background: white;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Cache la scrollbar sur certains navigateurs mais garde la fonctionnalité */
.testimonials-container::-webkit-scrollbar {
  height: 6px;
}

.testimonials-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.testimonials-container::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .testimonials-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.dots-navigation {
  display: none;
}

.dots-navigation {
  display: none; /* Cache les points s'ils ne sont pas nécessaires */
}

@media (max-width: 992px) {
  .testimonials-container {
    flex-direction: column;
    padding: 1rem;
    gap: 2rem;
  }
  
  .testimonial-card {
    width: 100%;
    margin-bottom: 1rem;
  }
}

.testimonial-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 0 12px rgba(0, 0, 0, 0.06);
}

.testimonial-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 1.5rem 0.5rem;
}

.client-info {
  flex: 1;
}

.client-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
  color: #1a202c;
}

.client-company {
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 0.5rem;
}

.rating {
  display: flex;
  font-size: 1.1rem;
  color: #FFC107;
}

.testimonial-body {
  padding: 0.5rem 1.5rem 2rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #4A5568;
  flex: 1;
}

.testimonial-border {
  height: 5px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  width: 100%;
}


.testimonials-section {
  padding: 2rem 0;
  margin: 2rem 0;
  position: relative;
}

.testimonials-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;
  position: relative;
  z-index: 1;
}

/* Call to Action */
.cta-container {
text-align: center;
margin: 3rem 0;
}

.cta-button {
background: linear-gradient(to right, #2563EB, #7C3AED);
color: white;
font-size: 1.25rem;
font-weight: bold;
padding: 1rem 2.5rem;
border: none;
border-radius: 2rem;
cursor: pointer;
box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
transition: transform 0.3s ease, box-shadow 0.3s ease;
text-decoration: none;
display: inline-block;
}

.cta-button:hover {
transform: translateY(-2px);
box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Responsive Adjustments */
/* Responsive Adjustments */
@media (max-width: 768px) {
  .header-content {
    padding: 4rem 1rem 2rem;
  }

  .main-title {
    font-size: 2rem;
  }

  .content-container {
    padding: 0 1rem;
  }

  .advantages-grid {
    grid-template-columns: 1fr;
  }

  .text-and-illustration {
    flex-direction: column;
  }

  .youtube-container {
    height: 200px; /* Hauteur réduite sur mobile */
  }
  
  /* Intro Card - Fix pour la section d'introduction */
  .intro-card .text-and-illustration {
    flex-direction: column;
  }
  
  .intro-card .text-content {
    width: 100%;
    min-width: 100%;
    margin-bottom: 1rem;
  }
  
  .intro-card .youtube-container {
    width: 100%;
    min-width: 100%;
    margin-top: 1rem;
  }
  
  .intro-card .section-title {
    font-size: 1.5rem;
    text-align: center;
  }

  .testimonials-container {
    padding: 1rem;
    flex-direction: column;
    gap: 2rem;
  }

  .testimonial-card {
    min-width: 100%;
  }

  .testimonials-title {
    font-size: 2rem;
    margin-bottom: 2.5rem;
  }
}


/* Dashboard Illustration */
.dashboard-illustration {
  margin: 2rem 0 3rem;
}

.illustration-container {
  display: flex;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
}

.dashboard-image {
  width: 100%;
  max-width: 800px;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;
}

.dashboard-image:hover {
  transform: scale(1.02);
}

@media (max-width: 768px) {
  .dashboard-illustration {
    margin: 1.5rem 0 2rem;
  }
}
</style>